import { makeStyles } from '@material-ui/core';
import { Done } from '@material-ui/icons';
import Close from '@material-ui/icons/Close';
import Avatar from '@mui/material/Avatar';
import Icon from '@mui/material/Icon';
import { Breadcrumb, Layout } from 'antd';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import CButton from 'src/component/Button';
import Copy from 'src/component/Copy';
import Loader from 'src/component/Loader/Loader';
import camelCaseToTitleCase from 'src/helpers/camelCaseToTitleCase';
import { useUpdateCustomer } from 'src/reactQuery/customers/mutations/useUpdateCustomer';
import { useGetCustomerQuery } from 'src/reactQuery/customers/queries/useGetCustomer';
import { isCurrentProjectIntranet } from 'src/util/projectCheckers';
import CustomerDetailEditModal from './CustomerDetailEditModal';

import CustomersDetailsTagsModal from './CustomerDetailTagsModal';
import { sortByObjValues, valueType } from './helpers';
import Devices from './sections/Devices';
import Inventory from './sections/Inventory';
import Profile from './sections/Profile';
import ArrowNextIcon from '../../../asset/arrow-next.svg';

import NoxTable from '../../../component/Table';

const useStyles = makeStyles({
  actionBar: {
    border: '1px solid #CAD8FF',
    display: 'flex',
    justifyContent: 'space-evenly',
    marginTop: '50px',
    padding: '35px 0px',
    width: '100%'
  },
  cover: {
    height: '70px',
    width: '120px'
  },
  featuredOn: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 500,
    transform: 'translateY(-3px)'
  },
  gridBio: {
    gridColumn: '2',
    gridRow: '1 / span 2',
    maxWidth: '230px',
    textAlign: 'start'
  },
  gridContainer: {
    display: 'grid',
    gridRowGap: '25px',
    gridTemplateColumns: '33% 33% 33%',
    marginLeft: '90px',
    marginTop: '55px'
  },
  gridItem: {
    maxWidth: '230px',
    textAlign: 'start'
  },
  gridItem2: {
    gridColumn: '1/-1',
    textAlign: 'start'
  },
  p: {
    color: '#9BA0AD',
    fontFamily: 'Poppins',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 'normal',
    lineHeight: '100%'
  },
  span: {
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 500
  },
  switch: {
    marginLeft: '12px'
  }
});

const restrictedData = [
  'EmailVerificationToken',
  '0',
  'metadata',
  'imageUrl',
  'project',
  'fcmTokens',
  'deviceIds'
];

const CustomersDetail = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [customerDraft, setCustomerDraft] = useState({});

  const { customerId } = useParams();
  const classes = useStyles();

  const currentProject = useSelector(({ project }) => project.currentProject);
  const providers = useSelector(({ provider }) => provider.providers);
  const resolvedProviders = providers.map((el) => el?.toLowerCase());

  const isCurrentProjectCapstone = currentProject._id === '627286c61f83a577456beb1e';

  const { data: { customer = {} } = {}, isLoading, isError, refetch } = useGetCustomerQuery(
    customerId,
    currentProject._id
  );

  const customerMutation = useUpdateCustomer({
    onSuccess: () => refetch()
  });

  useEffect(() => {
    if (customer._id) {
      setCustomerDraft({ ...customer });
    }
  }, [customer]);

  function itemRender(route, params, routes) {
    const last = routes.indexOf(route) === routes.length - 1;
    return last ? (
      <span>{route.breadcrumbName}</span>
    ) : (
      <Link to={route.path}>{route.breadcrumbName}</Link>
    );
  }

  const routes = [
    {
      breadcrumbName: 'Customers',
      path: '/authentication/customers'
    },
    {
      breadcrumbName: customer?.firstName || '-',
      path: `/authentication/customers/customerId`
    }
  ];

  const onUpdate = () => {
    customerMutation.mutate({
      body: customerDraft,
      customerId: customer._id
    });

    setIsModalOpen(false);
  };

  const table = (searchBy) => {
    const columns = [
      {
        dataIndex: 'idNumber',
        key: 'idNumber',
        render: (idNumber) => {
          return <Copy text={idNumber} />;
        },
        sorter: (a, b) => a.idNumber.localeCompare(b.idNumber),
        title: <div style={{ maxWidth: '37px' }}>ID</div>
      },
      {
        dataIndex: 'firstName',
        key: 'firstName',
        render: (firstName) => {
          return <Copy text={firstName} />;
        },
        sorter: (a, b) => a.firstName.toString().localeCompare(b.firstName),
        title: 'First Name'
      },
      {
        dataIndex: 'lastName',
        key: 'lastName',
        render: (lastName) => {
          return <Copy text={lastName} />;
        },
        sorter: (a, b) => a.lastName.toString().localeCompare(b.lastName),
        title: 'Last Name'
      },
      {
        dataIndex: 'fullName',
        key: 'fullName',
        render: (fullName) => {
          return <Copy text={fullName} />;
        },
        sorter: (a, b) => new Date(a.fullName) - new Date(b.fullName),
        title: 'Full Name'
      },
      {
        dataIndex: 'email',
        key: 'email',
        render: (email) => {
          return <Copy text={email} />;
        },
        sorter: (a, b) => new Date(a.email) - new Date(b.email),
        title: 'Email'
      },
      {
        dataIndex: 'phone',
        key: 'phone',
        render: (phone) => {
          return <Copy text={phone} />;
        },
        sorter: (a, b) => new Date(a.phone) - new Date(b.phone),
        title: 'Phone'
      }
    ];

    const friendsData = (customerData) => {
      const data = [];
      const emptyArrayInCaseError = [];

      if (!customerData || customerData.length === 0 || !customerData.metadata?.friends)
        return emptyArrayInCaseError;

      customerData.metadata.friends.forEach(
        ({ idNumber, email, firstName, fullName, lastName, phone }, index) => {
          data.push({
            email,
            firstName,
            fullName: fullName || '',
            idNumber,
            key: index,
            lastName,
            phone
          });
        }
      );

      return data;
    };

    const friendsTableContent = friendsData(customer);

    return <NoxTable columns={columns} data={friendsTableContent} searchBy={searchBy} />;
  };

  const CustomerHeader = () => {
    const { _id } = customer;
    const { metadata: { imageUrl = '' } = {}, firstName = '' } = customer;

    return (
      <div style={{ alignItems: 'center', display: 'flex', gap: '2em', marginLeft: '90px' }}>
        <Avatar alt={firstName} src={imageUrl} sx={{ height: 56, width: 56 }} />
        <div style={{ textAlign: 'start' }}>
          <span className={classes.span}>ID</span>
          <p className={classes.p}>{_id || '-'}</p>
        </div>
      </div>
    );
  };

  const customerBody = (customer) => {
    return (
      <>
        {Object.keys(customer)
          .sort((a, b) => sortByObjValues(a, b, customer))
          .map((key, i) => {
            if (key === 'friends') {
              return (
                <div key={key} className={classes.gridItem2}>
                  <div
                    style={{
                      fontFamily: 'Poppins',
                      fontSize: '38px'
                    }}
                  >
                    Friends
                  </div>
                  <br />
                  {table(['fullName', 'email', 'id'])}
                </div>
              );
            }

            if (valueType(customer[key]) === 'array' && !restrictedData.includes(key)) {
              return (
                <div key={i} className={classes.gridItem}>
                  <p className={classes.p}>{camelCaseToTitleCase(key)}</p>
                  <span className={classes.span}>{customer[key].join(',') || '-'}</span>
                </div>
              );
            }

            if (customer[key] !== null && valueType(customer[key]) === 'object') {
              return customerBody(customer[key]);
            }

            if (restrictedData.includes(key)) {
              return false;
            }

            if (typeof customer[key] === 'boolean') {
              return (
                <div key={i} className={classes.gridItem}>
                  <p className={classes.p}>{camelCaseToTitleCase(key)}</p>
                  <span className={classes.span}>
                    {customer[key] ? <Icon component={Done} /> : <Icon component={Close} />}
                  </span>
                </div>
              );
            }

            return (
              <div key={i} className={classes.gridItem}>
                <p className={classes.p}>{camelCaseToTitleCase(key)}</p>
                <span className={classes.span}>{customer[key] || '-'}</span>
              </div>
            );
          })}
      </>
    );
  };

  if (isError) {
    return <strong>Oops! something went wrong</strong>;
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Layout style={{ background: 'white' }}>
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          padding: '35px 40px 0px 35px'
        }}
      >
        <Breadcrumb
          itemRender={itemRender}
          routes={routes}
          separator={<img alt="separator icon" src={ArrowNextIcon} />}
          style={{
            background: 'white',
            display: 'flex',
            justifyContent: 'flex-start'
          }}
        />
        <div>
          <CButton onClick={() => setShowTagsModal(true)} type="primary">
            Edit Tags
          </CButton>
          <CButton onClick={() => setIsModalOpen(true)} type="primary">
            Edit
          </CButton>
        </div>
      </div>
      <div style={{ marginTop: '55px', padding: '0px 40px 0px 35px' }}>
        <CustomerHeader />
        <div className={classes.gridContainer}>{customerBody(customer)}</div>
        {resolvedProviders.includes('e-commerce') ? (
          <Profile
            isCurrentProjectCapstone={isCurrentProjectCapstone}
            refetch={refetch}
            selectedCustomer={customer}
          />
        ) : null}
      </div>
      <CustomerDetailEditModal
        customer={customer}
        customerDraft={customerDraft}
        onUpdate={onUpdate}
        open={isModalOpen}
        setCustomerDraft={setCustomerDraft}
        setOpen={setIsModalOpen}
      />
      <CustomersDetailsTagsModal
        customerDraft={customerDraft}
        onSave={onUpdate}
        setCustomerDraft={setCustomerDraft}
        setShowModal={setShowTagsModal}
        showModal={showTagsModal}
        tags={customer.tags}
      />
      {isCurrentProjectIntranet ? <Inventory /> : null}
      {customer.lastUsedDevice ? <Devices data={customer.lastUsedDevice} /> : null}
    </Layout>
  );
};

export default CustomersDetail;
